import * as Yup from 'yup';

import dayjs from 'dayjs';
import {
	seasonChanged,
	selectCurrentSeason,
} from 'features/rules/redux/rules-slice';
import {
	useCreateSeason,
	useDuplicateSeasons,
	useOverlappedSeasons,
	useUpdateSeason,
} from 'features/rules/seasons/seasons-hooks';
import { Form, Formik } from 'formik';
import { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';

import type { SelectChangeEvent } from '@mui/material';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Typography,
} from '@mui/material';
import { Modal } from 'components/Modal/Modal';
import RouterPromptModal from 'components/RouterPromptModal/RouterPromptModal';
import { TwoPaneLayout } from 'components/two-pane-layout/two-pane-layout';
import { usePropertyContext } from 'context/propertyContext';
import { alertAdded } from 'features/alerts/redux/alerts-slice';
import { today } from 'features/dates/date-helpers';
import { useSeasons } from 'features/rules/rules-hooks';
import { useUpdateSeasonsPrioritiesMutation } from 'features/rules/seasons/gql/_gen_/prop-seasons.gql';
import { useUser } from 'features/users/context/userContext';
import type { PropSeason } from 'graphql/gql-types';
import {
	AutopilotOption,
	OccupancyType,
	PricingStrategy,
} from 'graphql/gql-types';
import { labels } from 'locales/en.label';
import { toast } from 'react-hot-toast';
import { SelectedDays } from 'types/SearchTypes';
import { SeasonPriority } from 'types/SeasonTypes';
import { z } from 'zod';
import { FormButtons } from '../form-buttons/form-buttons';
import { RuleTabs } from '../rule-tabs/rule-tabs';
import { OverlappedSeasons } from './overlapped-seasons';
import { SeasonForm } from './season-form';
import { SeasonList } from './season-list';

const formLabels = labels.add_form;

const SeasonFormSchema = z
	.object({
		id: z.string().nullable(),
		seasonName: z.string(),
		startDate: z.string().nullable(),
		endDate: z.string().nullable(),
		selectedDays: z.array(z.nativeEnum(SelectedDays)),
		highlightInMyView: z.boolean(),
		priority: z.coerce.number().min(2).max(5),
		// Additional fields for complete season copying
		minRate: z.number().nullable().optional(),
		maxRate: z.number().nullable().optional(),
		pricingStrategy: z.nativeEnum(PricingStrategy).nullable().optional(),
		derivedRateType: z.nativeEnum(OccupancyType).nullable().optional(),
		derivedRateOffset: z.number().nullable().optional(),
		priorityThresholdLow: z.number().nullable().optional(),
		priorityThresholdMed: z.number().nullable().optional(),
		priorityThresholdHigh: z.number().nullable().optional(),
		autopilotLow: z.nativeEnum(AutopilotOption).nullable().optional(),
		autopilotMed: z.nativeEnum(AutopilotOption).nullable().optional(),
		autopilotHigh: z.nativeEnum(AutopilotOption).nullable().optional(),
		// Track original season ID for copying competitor associations
		originalSeasonId: z.string().nullable().optional(),
	})
	.refine(
		(data) => {
			if (!data.startDate || !data.endDate) return true; // Allow empty dates

			const startDate = dayjs(data.startDate);
			const endDate = dayjs(data.endDate);

			// Allow same-day seasons (start date === end date) and multi-day seasons
			return endDate.isSameOrAfter(startDate);
		},
		{
			message: 'End date must be the same as or after start date',
			path: ['endDate'], // Point error to endDate field
		},
	);

export type SeasonFormValues = z.infer<typeof SeasonFormSchema>;

export const defaultValues: SeasonFormValues = {
	id: null,
	seasonName: '',
	startDate: null,
	endDate: null,
	selectedDays: [],
	highlightInMyView: true,
	priority: SeasonPriority.Low,
	// Default values for additional fields
	minRate: null,
	maxRate: null,
	pricingStrategy: null,
	derivedRateType: null,
	derivedRateOffset: null,
	priorityThresholdLow: null,
	priorityThresholdMed: null,
	priorityThresholdHigh: null,
	autopilotLow: null,
	autopilotMed: null,
	autopilotHigh: null,
	originalSeasonId: null,
};

interface LocationState {
	season: PropSeason;
}

export const Seasons = () => {
	const history = useHistory();
	const location = useLocation<LocationState>();
	const dispatch = useAppDispatch();
	const selectedSeason = useAppSelector(selectCurrentSeason);
	const {
		property: { propertyId },
	} = usePropertyContext();
	const { user } = useUser();
	const { createSeason, createSeasonLoading } = useCreateSeason();
	const { updateSeason, updateSeasonLoading } = useUpdateSeason();
	const getOverlappedSeasons = useOverlappedSeasons();
	const getDuplicateSeasons = useDuplicateSeasons();
	const { seasons } = useSeasons();
	const [updateSeasonsPriorities, { loading: updateSeasonPriorityLoading }] =
		useUpdateSeasonsPrioritiesMutation();

	const [tabsEnabled, setTabsEnabled] = useState(false);
	const [overlappedSeasons, setOverlappedSeasons] = useState<PropSeason[]>();
	const [duplicateSeasons, setDuplicateSeasons] = useState<PropSeason[]>();
	const [continueClicked, setContinueClicked] = useState(false);
	const [showSetupPrompt, setShowSetupPrompt] = useState(false);
	const [newSeasonName, setNewSeasonName] = useState('');

	if (
		location?.state?.season &&
		selectedSeason?.season_id !== location?.state?.season?.season_id
	) {
		const season = seasons?.find(
			(s) => s.season_id === location.state.season.season_id,
		);
		if (season) dispatch(seasonChanged(season));
	}

	const seasonValidationSchema = useMemo(
		() =>
			Yup.object({
				seasonName: Yup.string().required(formLabels.required),
				startDate: selectedSeason
					? Yup.date().nullable().required(formLabels.required)
					: Yup.date()
							.nullable()
							.min(today(), formLabels.start_date_err)
							.required(formLabels.required),
				endDate: Yup.date()
					.nullable()
					.required(formLabels.required)
					.test('date-range', formLabels.end_date_err, function (value) {
						const { startDate } = this.parent;
						if (!value || !startDate) return true; // Allow empty values
						return dayjs(value).isSameOrAfter(dayjs(startDate));
					}),
				selectedDays: Yup.array()
					.nullable()
					.min(1, formLabels.required_day_err),
				highlightInMyView: Yup.boolean(),
				priority: Yup.number(),
				// Additional optional fields for season copying
				minRate: Yup.number().nullable().optional(),
				maxRate: Yup.number().nullable().optional(),
				pricingStrategy: Yup.string().nullable().optional(),
				derivedRateType: Yup.string().nullable().optional(),
				derivedRateOffset: Yup.number().nullable().optional(),
				priorityThresholdLow: Yup.number().nullable().optional(),
				priorityThresholdMed: Yup.number().nullable().optional(),
				priorityThresholdHigh: Yup.number().nullable().optional(),
				autopilotLow: Yup.string().nullable().optional(),
				autopilotMed: Yup.string().nullable().optional(),
				autopilotHigh: Yup.string().nullable().optional(),
				originalSeasonId: Yup.string().nullable().optional(),
			}),
		[selectedSeason],
	);

	const createOrUpdateSeason = async (values: SeasonFormValues) => {
		let success = false;
		if (values.id) {
			const updatedSeason = await updateSeason(values);
			if (updatedSeason) success = true;
		} else {
			const newSeason = await createSeason(values);
			if (newSeason) {
				success = true;
				dispatch(seasonChanged(newSeason));
				// Show setup prompt for non-duplicate new seasons
				if (!values.originalSeasonId) {
					setNewSeasonName(newSeason.season_name ?? '');
					setShowSetupPrompt(true);
				}
			}
		}

		if (continueClicked) {
			if (success) {
				// timeout ensures season is updated in redux store before continuing
				setTimeout(() => {
					history.push('/rules/competitors');
				}, 0);
			} else {
				setContinueClicked(false);
			}
		}
	};

	const handleSubmit = (values: SeasonFormValues) => {
		const validatedValues = SeasonFormSchema.safeParse(values);

		if (!validatedValues.success) {
			toast.error(`Invalid form values: ${validatedValues.error.message}`);
			return;
		}

		const parsedValues = validatedValues.data;

		if (!parsedValues.id) {
			const duplicateSeasons = getDuplicateSeasons(parsedValues);
			if (duplicateSeasons) {
				setDuplicateSeasons(duplicateSeasons);
				return;
			}
		}
		const overlappedSeasons = getOverlappedSeasons(parsedValues);
		if (overlappedSeasons) {
			setOverlappedSeasons(overlappedSeasons);
			return;
		}
		createOrUpdateSeason(parsedValues);
	};

	const onSeasonPriorityChange = (
		id: string,
		event: SelectChangeEvent<number>,
	) => {
		const updated = overlappedSeasons?.map((season: PropSeason) => {
			// Skip changes to default season
			if (season.season_name === labels.rules.default_season_name) {
				return season; // Return unchanged
			}

			if (season.season_id === id) {
				return { ...season, season_priority: Number(event.target.value) };
			}
			return season;
		});
		setOverlappedSeasons(updated);
	};

	const onOverlappedModalConfirm = async (values: SeasonFormValues) => {
		if (!overlappedSeasons) return;

		// Filter out default seasons from updates
		const updatableSeasons = overlappedSeasons.filter(
			(season) => season.season_name !== labels.rules.default_season_name,
		);

		const nonOverlappingSeasons: any[] = [];
		const overlappingSeasons: any[] = [];

		for (const season of updatableSeasons) {
			const overlappedResult = await getOverlappedSeasons({
				id: season.season_id as string,
				seasonName: season.season_name as string,
				startDate: season.season_start as string,
				endDate: season.season_end as string,
				selectedDays: season.selected_days as SelectedDays[],
				priority: season.season_priority as SeasonPriority,
				highlightInMyView: season.highlight_myview as boolean,
			});

			if (
				overlappedResult === undefined &&
				Number(values.priority) !== season.season_priority
			) {
				nonOverlappingSeasons.push(season);
			} else {
				overlappingSeasons.push(season);
			}
		}
		if (nonOverlappingSeasons.length > 0) {
			await updateSeasonsPriorities({
				variables: {
					input: {
						propertyId,
						seasonsPriorities: nonOverlappingSeasons.map(
							({ season_name, season_id, season_priority }) => ({
								seasonName: season_name,
								seasonId: season_id,
								seasonPriority: season_priority,
							}),
						),
						updatedBy: user?.login_id,
					},
				},
			});
		}

		// Show user feedback when default season is protected
		if (overlappedSeasons.length > updatableSeasons.length) {
			dispatch(
				alertAdded(
					'info',
					'Default season priority remains protected at Lowest level.',
				),
			);
		}

		if (overlappingSeasons.length === 0) {
			setOverlappedSeasons(undefined);
			createOrUpdateSeason(values);
		} else {
			dispatch(
				alertAdded(
					'error',
					`${overlappingSeasons.length} seasons you are trying to update overlap with existing seasons.`,
				),
			);
			setOverlappedSeasons(overlappingSeasons);
		}
	};

	const loading = createSeasonLoading || updateSeasonLoading;

	return (
		<div>
			<RuleTabs
				tabsEnabled={tabsEnabled}
				loading={loading}
				showDefaultBadges={selectedSeason?.is_default_config === true}
			/>
			<Formik<SeasonFormValues>
				initialValues={defaultValues}
				validationSchema={seasonValidationSchema}
				onSubmit={handleSubmit}
			>
				{({ values, dirty, validateForm, submitForm }) => (
					<>
						<RouterPromptModal isBlocked={dirty && !continueClicked} />
						<Modal
							isShown={!!duplicateSeasons}
							hide={() => setDuplicateSeasons(undefined)}
							onCancel={() => setDuplicateSeasons(undefined)}
							onConfirm={() => setDuplicateSeasons(undefined)}
							isConfirmationModal
							confirmBtnText={labels.rules.ok}
							messageBody={labels.rules.already_exist_err}
						/>
						<Modal
							isShown={!!overlappedSeasons}
							hide={() => setOverlappedSeasons(undefined)}
							onCancel={() => setOverlappedSeasons(undefined)}
							onConfirm={() => onOverlappedModalConfirm(values)}
							isConfirmationModal
							confirmBtnText={labels.common.save}
							loading={updateSeasonPriorityLoading}
						>
							<OverlappedSeasons
								overlappedSeasons={overlappedSeasons!}
								onSeasonPriorityChange={onSeasonPriorityChange}
							/>
						</Modal>
						<Form>
							<TwoPaneLayout
								loading={loading}
								LeftSideComponent={<SeasonForm />}
								RightSideComponent={
									<SeasonList setTabsEnabled={setTabsEnabled} />
								}
							/>
							<FormButtons
								loading={loading}
								dirty={dirty}
								onContinueClick={async () => {
									if (selectedSeason && !dirty) {
										history.push('/rules/competitors');
										return;
									}
									const isValid =
										Object.keys(await validateForm()).length === 0;
									if (isValid) setContinueClicked(true);
									submitForm();
								}}
							/>
						</Form>
					</>
				)}
			</Formik>
			<Dialog open={showSetupPrompt} onClose={() => setShowSetupPrompt(false)}>
				<DialogContent>
					<Typography variant='h6' gutterBottom>
						{newSeasonName} created successfully!
					</Typography>
					<Typography>
						We've added your competitors with equal weights and set safe
						defaults. Review the Competitors, Rates, and Autopilot tabs to
						customize your settings.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setShowSetupPrompt(false)}>
						I'll do it later
					</Button>
					<Button
						variant='contained'
						onClick={() => {
							setShowSetupPrompt(false);
							history.push('/rules/competitors');
						}}
					>
						Review Competitors
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};
